import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Tag, Store, Package, Calendar, DollarSign, Percent,
  CheckCircle, XCircle, Clock, TrendingUp, Users, Edit, Loader,
  AlertTriangle, MapPin, Building
} from 'lucide-react';
import adminService, { Offer } from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const OfferDetails: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();

  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (offerId) {
      fetchOfferDetails();
    }
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      setLoading(true);
      const data = await adminService.getOfferById(Number(offerId));
      setOffer(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch offer details');
      navigate('/admin/offers');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!offer) return;

    try {
      setProcessing(true);
      await adminService.toggleOfferStatus({
        offer_id: offer.offer_id,
        is_active: offer.is_active === 0,
      });
      toast.success(`Offer ${offer.is_active === 0 ? 'activated' : 'deactivated'} successfully!`);
      fetchOfferDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to toggle offer status');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (offer: Offer) => {
    const now = new Date();
    const validFrom = new Date(offer.valid_from);
    const validUntil = new Date(offer.valid_until);
    
    if (offer.is_active === 0) {
      return (
        <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm font-semibold">
          Inactive
        </span>
      );
    }
    if (now < validFrom) {
      return (
        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
          Scheduled
        </span>
      );
    }
    if (now > validUntil) {
      return (
        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
          Expired
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
        Active
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: string | number) => {
    return `$${parseFloat(String(amount)).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="text-center py-20">
        <AlertTriangle size={64} className="text-secondary-300 mx-auto mb-4" />
        <p className="text-secondary-600 font-semibold mb-2">Offer not found</p>
        <button
          onClick={() => navigate('/admin/offers')}
          className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
        >
          Back to Offers
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/offers')}
            className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Offer Details</h1>
            <p className="text-secondary-600 mt-1">View and manage offer information</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/admin/offers/${offer.offer_id}/edit`)}
            className="bg-warning-50 hover:bg-warning-100 text-warning-600 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
          >
            <Edit size={18} />
            <span>Edit Offer</span>
          </button>
          <button
            onClick={handleToggleStatus}
            disabled={processing}
            className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50 ${
              offer.is_active === 1
                ? 'bg-danger-50 hover:bg-danger-100 text-danger-600'
                : 'bg-success-50 hover:bg-success-100 text-success-600'
            }`}
          >
            {processing ? (
              <Loader className="animate-spin" size={18} />
            ) : offer.is_active === 1 ? (
              <>
                <XCircle size={18} />
                <span>Deactivate</span>
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                <span>Activate</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Status Badge */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-secondary-600 mb-2">Current Status</p>
            {getStatusBadge(offer)}
          </div>
          <div className="text-right">
            <p className="text-sm text-secondary-600 mb-2">Offer ID</p>
            <p className="text-lg font-bold text-secondary-900">#{offer.offer_id}</p>
          </div>
        </div>
      </div>

      {/* Main Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-xl font-bold text-secondary-900 mb-6 flex items-center space-x-2">
              <Tag className="text-primary-500" size={24} />
              <span>Basic Information</span>
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-secondary-600">Title</label>
                <p className="text-lg font-semibold text-secondary-900 mt-1">{offer.title}</p>
              </div>
              {offer.description && (
                <div>
                  <label className="text-sm font-semibold text-secondary-600">Description</label>
                  <p className="text-secondary-700 mt-1">{offer.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-secondary-600">Offer Type</label>
                  <p className="text-secondary-900 mt-1 capitalize">{offer.offer_type.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-secondary-600">Discount Type</label>
                  <p className="text-secondary-900 mt-1 capitalize">{offer.discount_type}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Discount Details */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-xl font-bold text-secondary-900 mb-6 flex items-center space-x-2">
              <DollarSign className="text-success-500" size={24} />
              <span>Discount Details</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Percent className="text-primary-600" size={20} />
                  <span className="text-sm font-semibold text-primary-700">Discount Value</span>
                </div>
                <p className="text-2xl font-bold text-primary-900">
                  {offer.discount_type === 'percentage' 
                    ? `${offer.discount_value}%` 
                    : formatCurrency(offer.discount_value)}
                </p>
              </div>
              {offer.min_purchase_amount && (
                <div className="bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="text-warning-600" size={20} />
                    <span className="text-sm font-semibold text-warning-700">Min Purchase</span>
                  </div>
                  <p className="text-2xl font-bold text-warning-900">
                    {formatCurrency(offer.min_purchase_amount)}
                  </p>
                </div>
              )}
              {offer.max_discount_amount && (
                <div className="bg-gradient-to-br from-info-50 to-info-100 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <DollarSign className="text-info-600" size={20} />
                    <span className="text-sm font-semibold text-info-700">Max Discount</span>
                  </div>
                  <p className="text-2xl font-bold text-info-900">
                    {formatCurrency(offer.max_discount_amount)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Validity & Usage */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-xl font-bold text-secondary-900 mb-6 flex items-center space-x-2">
              <Calendar className="text-info-500" size={24} />
              <span>Validity & Usage</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-semibold text-secondary-600">Valid From</label>
                <p className="text-secondary-900 mt-1 flex items-center space-x-2">
                  <Calendar size={16} className="text-secondary-400" />
                  <span>{formatDate(offer.valid_from)}</span>
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Valid Until</label>
                <p className="text-secondary-900 mt-1 flex items-center space-x-2">
                  <Calendar size={16} className="text-secondary-400" />
                  <span>{formatDate(offer.valid_until)}</span>
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Usage Limit</label>
                <p className="text-secondary-900 mt-1">
                  {offer.usage_limit ? offer.usage_limit.toLocaleString() : 'Unlimited'}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-secondary-600">Total Usage</label>
                <p className="text-secondary-900 mt-1 flex items-center space-x-2">
                  <Users size={16} className="text-primary-500" />
                  <span className="font-semibold">{offer.total_usage || offer.usage_count || 0}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Business/Category Info */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-lg font-bold text-secondary-900 mb-4">Associated With</h2>
            <div className="space-y-4">
              {offer.business_name && (
                <div className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
                  <Store className="text-primary-600" size={20} />
                  <div>
                    <p className="text-xs text-secondary-600">Business</p>
                    <p className="font-semibold text-secondary-900">{offer.business_name}</p>
                  </div>
                </div>
              )}
              {offer.category_name && (
                <div className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
                  <Package className="text-success-600" size={20} />
                  <div>
                    <p className="text-xs text-secondary-600">Category</p>
                    <p className="font-semibold text-secondary-900">{offer.category_name}</p>
                  </div>
                </div>
              )}
              {!offer.business_name && !offer.category_name && (
                <p className="text-sm text-secondary-500">Available for all businesses and categories</p>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="text-lg font-bold text-secondary-900 mb-4">Timestamps</h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-secondary-600">Created At</p>
                <p className="text-sm text-secondary-900 mt-1">{formatDate(offer.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-secondary-600">Last Updated</p>
                <p className="text-sm text-secondary-900 mt-1">{formatDate(offer.updated_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferDetails;

